import { useState, useEffect, useRef } from 'react';

/**
 * Hook personnalisé pour la reconnaissance vocale (Speech Recognition)
 * Utilise l'API Web Speech du navigateur
 * 
 * Expose :
 * - isListening : booléen indiquant si l'écoute est active
 * - transcript : chaîne contenant le texte transcrit en temps réel
 * - startListening : fonction pour démarrer l'écoute
 * - stopListening : fonction pour arrêter l'écoute
 * - isSupported : booléen indiquant si l'API est supportée
 * - permissionDenied : booléen indiquant si la permission a été refusée
 */
const useSpeechRecognition = (onResult, onError) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Vérifier si l'API est supportée
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    // Créer une instance de SpeechRecognition
    const recognition = new SpeechRecognition();
    
    // Configuration
    recognition.continuous = false; // Arrêter après une pause
    recognition.interimResults = true; // Afficher les résultats intermédiaires pour le transcript en temps réel
    recognition.lang = 'fr-FR'; // Langue française

    // Gestionnaire d'événement : début de l'écoute
    recognition.onstart = () => {
      setIsListening(true);
      setPermissionDenied(false);
      setTranscript(''); // Réinitialiser le transcript au démarrage
    };

    // Gestionnaire d'événement : résultats de la reconnaissance
    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      // Parcourir tous les résultats
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          // Résultat final
          finalTranscript += transcript + ' ';
        } else {
          // Résultat intermédiaire (en temps réel)
          interimTranscript += transcript;
        }
      }

      // Mettre à jour le transcript avec les résultats intermédiaires et finaux
      const fullTranscript = finalTranscript + interimTranscript;
      setTranscript(fullTranscript.trim());

      // Si on a un résultat final, appeler le callback
      if (finalTranscript) {
        const finalText = finalTranscript.trim();
        // Garder le transcript final pour qu'il soit disponible après onend
        setTranscript(finalText);
        if (onResult) {
          onResult(finalText);
        }
      }
    };

    // Gestionnaire d'événement : erreurs
    recognition.onerror = (event) => {
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        setPermissionDenied(true);
        if (onError) {
          onError('Permission du microphone refusée. Veuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur.');
        }
      } else if (event.error === 'no-speech') {
        setTranscript('');
        if (onError) {
          onError('Aucune parole détectée. Veuillez réessayer.');
        }
      } else if (event.error === 'aborted') {
        // L'utilisateur a arrêté manuellement, pas d'erreur à afficher
        setTranscript('');
      } else {
        setTranscript('');
        if (onError) {
          onError(`Erreur de reconnaissance vocale : ${event.error}`);
        }
      }
    };

    // Gestionnaire d'événement : fin de l'écoute
    recognition.onend = () => {
      setIsListening(false);
      // Le transcript final est déjà mis à jour dans onresult
      // Il sera réinitialisé au prochain startListening
    };

    recognitionRef.current = recognition;

    // Nettoyage
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [onResult, onError]);

  /**
   * Démarre l'écoute vocale
   */
  const startListening = () => {
    if (!isSupported) {
      if (onError) {
        onError('La reconnaissance vocale n\'est pas supportée par votre navigateur. Veuillez utiliser Chrome, Edge ou Firefox.');
      }
      return;
    }

    if (recognitionRef.current && !isListening) {
      try {
        // Réinitialiser le transcript avant de démarrer
        setTranscript('');
        recognitionRef.current.start();
      } catch (error) {
        if (onError) {
          onError('Impossible de démarrer la reconnaissance vocale. Veuillez réessayer.');
        }
      }
    }
  };

  /**
   * Arrête l'écoute vocale
   */
  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  return {
    isListening,
    transcript,
    startListening,
    stopListening,
    isSupported,
    permissionDenied,
  };
};

export default useSpeechRecognition;

